import { useLighthouseReport } from '@/components/lighthouse/use-lhr';
import { mapNetworkRequests } from '@/components/lighthouse/waterfall-data';
import { Island } from '@/components/shell/Island';
import { SiteSwitcherCrumb } from '@/components/shell/SiteSwitcherCrumb';
import { useActiveSiteUuid } from '@/lib/active-site';
import type { BlockSelection, PartnerCatalog, VariantDeltas } from '@/lib/api-types';
import { useSite } from '@/lib/queries';
import { useAudits } from '@/lib/queries/audits';
import { useBlockPartnerCatalog, useCreateVariant } from '@/lib/queries/variants';
import { CaretDown, CaretRight } from '@phosphor-icons/react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { type ReactElement, useEffect, useMemo, useRef, useState } from 'react';

export const Route = createFileRoute('/_app/performance/variants/new')({
  component: NewVariantPage,
});

interface HeaderRow {
  name: string;
  value: string;
}

const inputClass =
  'h-9 w-full rounded-lg border border-[var(--color-line)] bg-[var(--color-paper-alt)] px-3 text-sm text-[var(--color-ink)] outline-none focus:border-[var(--color-accent-brand)]';

/** Native checkbox with an indeterminate ("some of this category") state. */
function TriCheckbox({
  checked,
  indeterminate,
  onChange,
  'aria-label': ariaLabel,
}: {
  checked: boolean;
  indeterminate: boolean;
  onChange: () => void;
  'aria-label': string;
}): ReactElement {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = indeterminate;
  }, [indeterminate]);
  return (
    <input
      ref={ref}
      type="checkbox"
      checked={checked}
      onChange={onChange}
      aria-label={ariaLabel}
      className="size-4 accent-[var(--color-accent-brand)]"
    />
  );
}

/**
 * Encode the picker state back into a BlockSelection. Fully-checked
 * categories are stored as category intent (partners added to the
 * catalog later join automatically); partially-checked categories are
 * stored as explicit per-partner blocks.
 */
function buildBlockSelection(
  catalog: PartnerCatalog,
  checked: Set<string>,
  customDomains: string[],
): BlockSelection | undefined {
  const categories: string[] = [];
  const overrides: Record<string, 'block' | 'allow'> = {};
  for (const cat of catalog.categories) {
    const members = catalog.partners.filter((p) => p.category === cat.key);
    if (members.length === 0) continue;
    const selected = members.filter((p) => checked.has(p.key));
    if (selected.length === members.length) {
      categories.push(cat.key);
    } else {
      for (const p of selected) overrides[p.key] = 'block';
    }
  }
  const hasOverrides = Object.keys(overrides).length > 0;
  if (categories.length === 0 && !hasOverrides && customDomains.length === 0) return undefined;
  return {
    ...(categories.length > 0 ? { categories } : {}),
    ...(hasOverrides ? { partner_overrides: overrides } : {}),
    ...(customDomains.length > 0 ? { custom_domains: customDomains } : {}),
  };
}

interface DetectedPartners {
  /** Partner keys observed in the latest successful audit's waterfall; null while unknown. */
  detected: Set<string> | null;
  /** True while the audits list / report fetch is still in flight. */
  pending: boolean;
}

/**
 * Which curated partners actually appear in the site's latest
 * successful audit — resolved from its waterfall (the same
 * network-requests data the audit page charts) so the picker leads
 * with what's really on the page instead of the full catalog. Degrades
 * to `detected: null` when the site has no successful audits or the
 * report fetch fails.
 */
function useDetectedPartners(
  siteUuid: string | null | undefined,
  catalog: PartnerCatalog | undefined,
): DetectedPartners {
  // Exclude variant runs server-side: a treatment audit had partners
  // deliberately blocked (detecting against it under-reports), and on a
  // site with an active variant those runs bury every baseline audit —
  // one variant batch is the whole grid, so a client-side skip over a
  // 20-row page finds nothing.
  const audits = useAudits(
    { site_uuid: siteUuid ?? '', limit: 20, exclude_variant: true },
    { enabled: !!siteUuid },
  );
  const latest = audits.data?.audits.find((a) => a.status === 'success') ?? null;
  const lhr = useLighthouseReport(latest?.id ?? null);
  const detected = useMemo(() => {
    if (!catalog || !lhr.data) return null;
    const urls = mapNetworkRequests(lhr.data).map((r) => r.url);
    const found = new Set<string>();
    for (const p of catalog.partners) {
      if (p.domains.some((d) => urls.some((u) => u.includes(d)))) found.add(p.key);
    }
    return found;
  }, [catalog, lhr.data]);
  const pending = audits.isLoading || (latest !== null && lhr.isLoading);
  return { detected, pending };
}

function PartnerPicker({
  catalog,
  checked,
  onToggle,
  onToggleCategory,
}: {
  catalog: PartnerCatalog;
  checked: Set<string>;
  onToggle: (key: string) => void;
  onToggleCategory: (categoryKey: string) => void;
}): ReactElement {
  return (
    <div className="space-y-2">
      {catalog.categories.map((cat) => {
        const members = catalog.partners.filter((p) => p.category === cat.key);
        if (members.length === 0) return null;
        const selected = members.filter((p) => checked.has(p.key)).length;
        const all = selected === members.length;
        return (
          <div key={cat.key} className="rounded-lg border border-[var(--color-line)]">
            <label className="flex cursor-pointer items-center gap-3 px-3 py-2.5 transition-colors hover:bg-[var(--color-paper-alt)]/40">
              <TriCheckbox
                checked={all}
                indeterminate={selected > 0 && !all}
                onChange={() => onToggleCategory(cat.key)}
                aria-label={`Toggle all ${cat.label}`}
              />
              <span className="text-sm font-medium text-[var(--color-ink)]">{cat.label}</span>
              <span className="ml-auto text-xs tabular-nums text-[var(--color-mute)]">
                {selected}/{members.length}
              </span>
            </label>
            <div className="space-y-0.5 border-t border-[var(--color-line-soft)] p-2">
              {members.map((p) => (
                <label
                  key={p.key}
                  className="flex cursor-pointer items-center gap-3 rounded-md px-2 py-1.5 transition-colors hover:bg-[var(--color-paper-alt)]/40"
                >
                  <input
                    type="checkbox"
                    checked={checked.has(p.key)}
                    onChange={() => onToggle(p.key)}
                    className="size-4 accent-[var(--color-accent-brand)]"
                  />
                  <span className="shrink-0 text-sm text-[var(--color-ink)]">{p.name}</span>
                  <span
                    className="min-w-0 flex-1 truncate text-right font-mono text-[10px] text-[var(--color-mute)]"
                    title={p.domains.join(', ')}
                  >
                    {p.domains.join(' · ')}
                  </span>
                </label>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/**
 * The waterfall-driven half of the picker: only the partners the site's
 * latest audit actually requested. No detection data (no audits yet /
 * report fetch failed) → a quiet note; detection ran but found nothing
 * → the honest "nothing to remove here" verdict that stops a pointless
 * variant before it's created.
 */
function DetectedPartnerList({
  catalog,
  detected,
  checked,
  onToggle,
}: {
  catalog: PartnerCatalog;
  detected: Set<string> | null;
  checked: Set<string>;
  onToggle: (key: string) => void;
}): ReactElement {
  if (detected === null) {
    return (
      <p className="rounded-lg border border-[var(--color-line-soft)] bg-[var(--color-paper-alt)]/40 px-3 py-2 text-xs text-[var(--color-mute)]">
        No audit data yet to detect this site's third parties — pick from the catalog below.
      </p>
    );
  }
  const members = catalog.partners.filter((p) => detected.has(p.key));
  if (members.length === 0) {
    return (
      <p className="rounded-lg border border-[var(--color-warning)]/30 bg-[var(--color-warning)]/5 px-3 py-2 text-xs text-[var(--color-ink-soft)]">
        <span className="font-medium text-[var(--color-warning)]">
          No known third parties detected
        </span>{' '}
        in this site's latest audit — a partner-blocking variant would have nothing to remove.
        Headers, URL params, or custom domains below may be a better fit.
      </p>
    );
  }
  return (
    <div className="rounded-lg border border-[var(--color-line)]">
      <div className="border-b border-[var(--color-line-soft)] px-3 py-2 text-xs font-medium uppercase tracking-[0.06em] text-[var(--color-mute)]">
        Detected in the latest audit · {members.length}
      </div>
      <div className="space-y-0.5 p-2">
        {members.map((p) => (
          <label
            key={p.key}
            className="flex cursor-pointer items-center gap-3 rounded-md px-2 py-1.5 transition-colors hover:bg-[var(--color-paper-alt)]/40"
          >
            <input
              type="checkbox"
              checked={checked.has(p.key)}
              onChange={() => onToggle(p.key)}
              className="size-4 accent-[var(--color-accent-brand)]"
            />
            <span className="shrink-0 text-sm text-[var(--color-ink)]">{p.name}</span>
            <span
              className="min-w-0 flex-1 truncate text-right font-mono text-[10px] text-[var(--color-mute)]"
              title={p.domains.join(', ')}
            >
              {p.domains.join(' · ')}
            </span>
          </label>
        ))}
      </div>
    </div>
  );
}

function NewVariantPage(): ReactElement {
  const siteUuid = useActiveSiteUuid();
  const site = useSite(siteUuid);
  const navigate = useNavigate();
  const create = useCreateVariant();
  const catalog = useBlockPartnerCatalog();
  const detection = useDetectedPartners(siteUuid, catalog.data);
  const [name, setName] = useState('');
  // null until the catalog + detection settle, then defaults to the
  // partners actually observed in the latest audit — checking the whole
  // catalog oversold what the variant would do on sites that don't
  // carry those partners. No audits / report fetch failure → fall back
  // to the old every-partner preset.
  const [checked, setChecked] = useState<Set<string> | null>(null);
  const [catalogOpen, setCatalogOpen] = useState(false);
  const [customDomains, setCustomDomains] = useState('');
  const [headers, setHeaders] = useState<HeaderRow[]>([]);
  const [urlQuery, setUrlQuery] = useState('');

  useEffect(() => {
    if (!catalog.data || checked !== null || detection.pending) return;
    setChecked(new Set(detection.detected ?? catalog.data.partners.map((p) => p.key)));
  }, [catalog.data, checked, detection]);

  function togglePartner(key: string): void {
    setChecked((prev) => {
      const next = new Set(prev ?? []);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  function toggleCategory(categoryKey: string): void {
    if (!catalog.data) return;
    const members = catalog.data.partners.filter((p) => p.category === categoryKey);
    setChecked((prev) => {
      const next = new Set(prev ?? []);
      const all = members.every((p) => next.has(p.key));
      for (const p of members) {
        if (all) next.delete(p.key);
        else next.add(p.key);
      }
      return next;
    });
  }

  function setHeader(i: number, patch: Partial<HeaderRow>): void {
    setHeaders((prev) => prev.map((row, idx) => (idx === i ? { ...row, ...patch } : row)));
  }

  function buildDeltas(): VariantDeltas {
    const deltas: VariantDeltas = {};
    const domains = customDomains
      .split('\n')
      .map((d) => d.trim())
      .filter(Boolean);
    if (catalog.data && checked) {
      const blocked = buildBlockSelection(catalog.data, checked, domains);
      if (blocked) deltas.blocked_partners = blocked;
    } else if (domains.length > 0) {
      deltas.blocked_partners = { custom_domains: domains };
    }
    const headerEntries = headers
      .map((h) => [h.name.trim(), h.value.trim()] as const)
      .filter(([n, v]) => n.length > 0 && v.length > 0);
    if (headerEntries.length > 0) {
      deltas.extra_headers = Object.fromEntries(headerEntries);
    }
    const query = urlQuery.trim().replace(/^\?/, '');
    if (query) deltas.url_query = query;
    return deltas;
  }

  async function onSubmit(): Promise<void> {
    if (!siteUuid || !name.trim()) return;
    const created = await create.mutateAsync({
      siteUuid,
      body: { name: name.trim(), deltas: buildDeltas() },
    });
    navigate({
      to: '/performance/variants/$variantUuid',
      params: { variantUuid: created.uuid },
    });
  }

  const breadcrumb = [
    { label: site.data?.name ?? siteUuid ?? '', render: <SiteSwitcherCrumb /> },
    { label: 'Variants', href: '/performance/variants' },
    { label: 'New' },
  ];

  return (
    <Island breadcrumb={breadcrumb}>
      <div className="space-y-6">
        <div className="space-y-1">
          <h1 className="text-base font-semibold text-[var(--color-ink)]">New variant</h1>
          <p className="text-sm text-[var(--color-mute)]">
            Re-run this site's audits with modified page conditions, compared against your baseline.
          </p>
        </div>

        <section className="space-y-5 rounded-2xl border border-[var(--color-line)] bg-[var(--color-paper)] p-5">
          <div className="space-y-2">
            <label htmlFor="var-name" className="text-sm font-medium text-[var(--color-ink)]">
              Name
            </label>
            <input
              id="var-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Without analytics"
              className={inputClass}
            />
          </div>
        </section>

        <section className="space-y-5 rounded-2xl border border-[var(--color-line)] bg-[var(--color-paper)] p-5">
          <div className="space-y-1">
            <h2 className="text-base font-semibold text-[var(--color-ink)]">
              Remove third parties
            </h2>
            <p className="text-sm text-[var(--color-mute)]">
              Pre-selected from what this site's latest audit actually loaded. We never block
              payments, consent, CDNs or fonts.
            </p>
          </div>
          {catalog.data && checked ? (
            <>
              <DetectedPartnerList
                catalog={catalog.data}
                detected={detection.detected}
                checked={checked}
                onToggle={togglePartner}
              />
              <div className="rounded-lg border border-[var(--color-line)]">
                <button
                  type="button"
                  onClick={() => setCatalogOpen((o) => !o)}
                  aria-expanded={catalogOpen}
                  className="flex w-full items-center gap-2 px-3 py-2.5 text-left outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent-brand)]/40"
                >
                  {catalogOpen ? (
                    <CaretDown aria-hidden size={14} className="text-[var(--color-mute)]" />
                  ) : (
                    <CaretRight aria-hidden size={14} className="text-[var(--color-mute)]" />
                  )}
                  <span className="text-sm font-medium text-[var(--color-ink)]">All partners</span>
                  <span className="ml-auto text-xs tabular-nums text-[var(--color-mute)]">
                    {checked.size}/{catalog.data.partners.length} selected
                  </span>
                </button>
                {catalogOpen && (
                  <div className="border-t border-[var(--color-line-soft)] p-3">
                    <PartnerPicker
                      catalog={catalog.data}
                      checked={checked}
                      onToggle={togglePartner}
                      onToggleCategory={toggleCategory}
                    />
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="space-y-2" aria-hidden="true">
              <div className="h-24 rounded-lg border border-[var(--color-line-soft)] bg-[var(--color-paper-alt)]/40" />
            </div>
          )}
          <div className="space-y-2">
            <label
              htmlFor="var-domains"
              className="text-xs font-medium uppercase tracking-[0.06em] text-[var(--color-mute)]"
            >
              Advanced — block specific domains (one per line)
            </label>
            <textarea
              id="var-domains"
              value={customDomains}
              onChange={(e) => setCustomDomains(e.target.value)}
              placeholder="cdn.weird-vendor.io"
              rows={3}
              className="w-full rounded-lg border border-[var(--color-line)] bg-[var(--color-paper-alt)] px-3 py-2 text-sm text-[var(--color-ink)] outline-none focus:border-[var(--color-accent-brand)]"
            />
          </div>
        </section>

        <section className="space-y-5 rounded-2xl border border-[var(--color-line)] bg-[var(--color-paper)] p-5">
          <div className="space-y-1">
            <h2 className="text-base font-semibold text-[var(--color-ink)]">Send headers</h2>
            <p className="text-sm text-[var(--color-mute)]">
              HTTP headers added to every request — e.g. a server-side feature-flag header.
            </p>
          </div>
          {headers.length > 0 && (
            <div className="space-y-2">
              {headers.map((h, i) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: rows are positional editor state
                <div key={i} className="flex items-center gap-2">
                  <input
                    value={h.name}
                    onChange={(e) => setHeader(i, { name: e.target.value })}
                    placeholder="X-FF-Checkout"
                    className={`${inputClass} font-mono`}
                    aria-label={`Header ${i + 1} name`}
                  />
                  <input
                    value={h.value}
                    onChange={(e) => setHeader(i, { value: e.target.value })}
                    placeholder="new-flow"
                    className={`${inputClass} font-mono`}
                    aria-label={`Header ${i + 1} value`}
                  />
                  <button
                    type="button"
                    onClick={() => setHeaders((prev) => prev.filter((_, idx) => idx !== i))}
                    className="shrink-0 text-xs text-[var(--color-mute)] hover:text-[var(--color-ink)]"
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          )}
          <button
            type="button"
            onClick={() => setHeaders((prev) => [...prev, { name: '', value: '' }])}
            className="rounded-lg border border-[var(--color-line)] px-3 py-1.5 text-sm font-medium text-[var(--color-ink)]"
          >
            Add header
          </button>
        </section>

        <section className="space-y-5 rounded-2xl border border-[var(--color-line)] bg-[var(--color-paper)] p-5">
          <div className="space-y-1">
            <h2 className="text-base font-semibold text-[var(--color-ink)]">Modify URL</h2>
            <p className="text-sm text-[var(--color-mute)]">
              Query params appended to every page URL — e.g. a client-side flag like{' '}
              <span className="font-mono text-xs">v=2</span>.
            </p>
          </div>
          <input
            value={urlQuery}
            onChange={(e) => setUrlQuery(e.target.value)}
            placeholder="v=2&flag=on"
            className={`${inputClass} font-mono`}
            aria-label="URL query params"
          />
        </section>

        <div className="flex items-center gap-3">
          <button
            type="button"
            disabled={!siteUuid || !name.trim() || create.isPending}
            onClick={onSubmit}
            className="rounded-lg border border-transparent bg-[var(--primary)] px-4 py-2 text-sm font-medium text-[var(--primary-foreground)] disabled:opacity-50"
          >
            {create.isPending ? 'Creating…' : 'Create variant'}
          </button>
          <button
            type="button"
            onClick={() => navigate({ to: '/performance/variants' })}
            className="text-sm text-[var(--color-mute)] hover:text-[var(--color-ink)]"
          >
            Cancel
          </button>
        </div>
      </div>
    </Island>
  );
}
